<template>
  <div class="container">

    <!-- LOADING -->
    <div v-if="fetchData" class="loading">
      <load-data />
    </div>

    <!-- ================= ADD / EDIT EXPERIMENT MODAL ================= -->
    <b-modal
      id="experimentModel"
      centered
      hide-footer
      hide-header
      no-close-on-backdrop
      no-close-on-keyboard
      size="md"
    >
      <b-form-group label="No">
        <b-form-input v-model="experiment.no" />
      </b-form-group>

      <b-form-group label="Experiment">
        <b-form-input v-model="experiment.name" />
      </b-form-group>

      <b-form-group label="Description">
        <b-form-input v-model="experiment.description" />
      </b-form-group>

      <b-form-group label="Procedure">
        <b-form-input v-model="experiment.procedures" />
      </b-form-group>

      <b-form-group label="Course Outcomes">
        <b-form-checkbox-group v-model="experiment.cos">
          <b-form-checkbox
            v-for="co in courseOutcomeOptions"
            :key="co.value"
            :value="co.value"
          >
            {{ co.text }}
          </b-form-checkbox>
        </b-form-checkbox-group>
      </b-form-group>

      <b-button variant="primary" @click="updateLabExperiment">
        {{ updateExperimentButton }}
      </b-button>
      <b-button variant="danger" @click="closeExperimentModal">
        Cancel
      </b-button>
    </b-modal>

    <!-- ================= DELETE CONFIRM ================= -->
    <b-modal
      v-model="experimentForceDeleteData.showDeleteConfirm"
      title="Confirm Deletion"
      hide-footer
    >
      <p class="text-danger">
        {{ experimentForceDeleteData.deleteMessage }}
      </p>

      <b-form-group label="Type DELETE to confirm">
        <b-form-input
          v-model="experimentForceDeleteData.deleteConfirmText"
        />
      </b-form-group>

      <b-button
        variant="danger"
        :disabled="experimentForceDeleteData.deleteConfirmText !== 'DELETE'"
        @click="forceDeleteLabExperiment"
      >
        Confirm Delete
      </b-button>
    </b-modal>

    <!-- ================= CARD ================= -->
    <b-card>
      <div class="d-flex align-items-center mb-3">
        <b-button
          variant="info"
          class="mr-3"
          @click="$bvModal.show('addExperimentOptionsModal')"
        >
          Add Experiments
        </b-button>
      </div>

      <!-- ADD OPTIONS -->
      <b-modal id="addExperimentOptionsModal" centered hide-footer title="Add Experiments">
        <ul>
          <li>
            Copy from University Resources
            <span class="copy-link" @click="openCopy('template')">
              Click here to proceed
            </span>
          </li>
          <li>
            Copy from previous academic year
            <span class="copy-link" @click="openCopy('myinstitute')">
              Click here to proceed
            </span>
          </li>
          <li>
            Or Enter Manually
            <span class="copy-link" @click="createLabExperiment">
              Click here to proceed
            </span>
          </li>
        </ul>
      </b-modal>

      <!-- COPY MODAL -->
      <b-modal id="copyCoursePropertiesModel" centered hide-footer hide-header>
        <CopyCourseProperties
          :copy_from_suggestion="copy_from"
          :course_id="course_id"
          @success="propertyCopyCompleted"
          @cancel="$bvModal.hide('copyCoursePropertiesModel')"
        />
      </b-modal>

      <!-- ================= TABLE ================= -->
      <table
        v-if="labExperiments.length"
        class="table table-striped table-bordered"
      >
        <thead class="thead-dark">
          <tr>
            <th>No</th>
            <th>Name</th>
            <th>Description</th>
            <th>Procedure</th>
            <th>COs</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="exp in visibleExperiments"
            :key="exp.id"
          >
            <td>{{ exp.no }}</td>
            <td>{{ exp.name }}</td>
            <td>{{ exp.description }}</td>
            <td>{{ exp.procedures }}</td>
            <td>
              <span
                v-for="(co, index) in exp.course_outcomes"
                :key="co.id"
              >
                {{ co.code }}<span v-if="index < exp.course_outcomes.length - 1">, </span>
              </span>
            </td>
            <td>
              <b-button size="sm" variant="info" @click="editLabExperiment(exp)">
                Edit
              </b-button>
              <b-button size="sm" variant="danger" @click="deleteLabExperiment(exp)">
                Delete
              </b-button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- SHOW MORE -->
      <div v-if="canShowMore" class="text-center mt-2">
        <b-link @click="showAllExperiments = !showAllExperiments">
          {{ showAllExperiments ? 'Show less' : 'Show more' }}
        </b-link>
      </div>

      <div v-if="!labExperiments.length" class="text-center text-muted py-4">
        No experiments added yet
      </div>
    </b-card>

    <LabContinuousEvaluationParameter :course_id="course_id" />
    <LabInternalExamParameter :course_id="course_id" />
  </div>
</template>

<script>
import axios from "axios";
import LabContinuousEvaluationParameter from "./LabContinuousEvaluationParameter.vue";
import LabInternalExamParameter from "./LabInternalExamParameter.vue";
import CopyCourseProperties from "../templates/Dashboard/Course/CopyCourseProperties.vue";

export default {
  props: {
    course_id: {
      type: Number,
      required: true
    }
  },

  components: {
    CopyCourseProperties,
    LabContinuousEvaluationParameter,
    LabInternalExamParameter
  },

  data() {
    return {
      fetchData: false,
      labExperiments: [],
      showAllExperiments: false,
      copy_from: null,

      experiment: this.defaultExperiment(),

      courseOutcomeOptions: [],
      updateExperimentButton: "Save",

      experimentForceDeleteData: {
        showDeleteConfirm: false,
        deleteMessage: "",
        deleteConfirmText: "",
        experimentToDelete: null
      }
    };
  },

  computed: {
    visibleExperiments() {
      return this.showAllExperiments
        ? this.labExperiments
        : this.labExperiments.slice(0, 2);
    },
    canShowMore() {
      return this.labExperiments.length > 2;
    }
  },

  async created() {
    try {
      this.fetchData = true;
      await this.getLabExperiments();
    } finally {
      this.fetchData = false;
    }
  },

  methods: {
    defaultExperiment() {
      return {
        id: "",
        no: "",
        name: "",
        description: "",
        procedures: "",
        cos: [],
        course_id: this.course_id
      };
    },

    async getLabExperiments() {
      const url = `${this.$store.getters.getAPIKey.mainAPI}/labexperiments?course=${this.course_id}`;
      const res = await axios.get(url);
      this.labExperiments = res.data;
    },

    createLabExperiment() {
      this.updateExperimentButton = "Save";
      this.experiment = this.defaultExperiment();
      this.$bvModal.hide("addExperimentOptionsModal");
      this.$bvModal.show("experimentModel");
      this.getcourseOutcomes();
    },

    editLabExperiment(exp) {
      this.experiment = {
        ...exp,
        cos: exp.course_outcomes?.map(c => c.id) || []
      };
      this.updateExperimentButton = "Update";
      this.$bvModal.show("experimentModel");
      this.getcourseOutcomes();
    },

    async updateLabExperiment() {
      const base = this.$store.getters.getAPIKey.mainAPI;
      const url = this.experiment.id
        ? `${base}/labexperiments/${this.experiment.id}`
        : `${base}/labexperiments`;

      await axios[this.experiment.id ? "put" : "post"](url, this.experiment);
      this.closeExperimentModal();
      await this.getLabExperiments();
    },

    closeExperimentModal() {
      this.$bvModal.hide("experimentModel");
      this.experiment = this.defaultExperiment();
      this.updateExperimentButton = "Save";
    },

    async deleteLabExperiment(exp) {
      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/labexperiments/${exp.id}`;
        const res = await axios.delete(url);

        if (!res.data.success) {
          this.experimentForceDeleteData = {
            showDeleteConfirm: true,
            deleteMessage: res.data.message,
            deleteConfirmText: "",
            experimentToDelete: exp
          };
          return;
        }

        await this.getLabExperiments();
        this.$toast.warning("Experiment Deleted");
      } catch {
        this.$toast.error("Failed to delete experiment");
      }
    },

    async forceDeleteLabExperiment() {
      const exp = this.experimentForceDeleteData.experimentToDelete;
      const url = `${this.$store.getters.getAPIKey.mainAPI}/labexperiments/${exp.id}/force-delete`;

      await axios.delete(url);
      await this.getLabExperiments();

      this.experimentForceDeleteData = {
        showDeleteConfirm: false,
        deleteMessage: "",
        deleteConfirmText: "",
        experimentToDelete: null
      };
    },

    async getcourseOutcomes() {
      if (this.courseOutcomeOptions.length) return;

      const url = this.$store.getters.getAPIKey.getCourseOutcomes.replace(
        "course_id",
        this.course_id
      );
      const res = await axios.get(url);

      this.courseOutcomeOptions = res.data.map(co => ({
        value: co.id,
        text: co.code
      }));
    },

    openCopy(type) {
      this.copy_from = type;
      this.$bvModal.hide("addExperimentOptionsModal");
      this.$bvModal.show("copyCoursePropertiesModel");
    },

    propertyCopyCompleted() {
      this.$bvModal.hide("copyCoursePropertiesModel");
      this.getLabExperiments();
    }
  }
};
</script>

<style scoped>
.copy-link {
  color: #2929aa;
  text-decoration: underline;
  cursor: pointer;
}
</style>
